/**
 * Ledger API — positions browse/hide, imports (CSV + Flex), cash transactions.
 * Import lives inside the Ledger page; CSV upload goes multipart.
 */
import { apiGet, apiPost, apiDelete, client } from './client'
import type { TrackerPosition } from './tracker'

export interface LedgerPosition extends TrackerPosition {
  is_hidden: boolean
  broker_account_id: number | null
  tracker_trade_id: number | null
  tracker_trade_name: string | null
  import_batch: string | null
  notes: string | null
  days_held: number | null
}

export interface LedgerPositionsResponse {
  count: number
  positions: LedgerPosition[]
  limit: number
  offset: number
}

export interface LedgerSummary {
  open_count: number
  closed_count: number
  unassigned_count: number
  assigned_count: number
  hidden_count: number
  total_commissions: number
}

export interface CashTransaction {
  id: number
  transaction_id: string
  type: string
  raw_type: string
  amount: number
  currency: string
  report_date: string
  settle_date: string | null
  description: string | null
  broker_account_id: number
}

export interface ImportPreviewResult {
  success: boolean
  imported: number
  duplicates_skipped: number
  updated: number
  errors: number
  error_details: string[]
  warnings: string[]
  preview_data: Array<{
    row: number
    symbol: string
    underlying: string
    type: string
    quantity: number
    buy_sell: string
    price: number
    commission: number
    date: string
    time: string
    account: string
    conid: string
    is_duplicate: boolean
    is_update: boolean
    changes: string[]
  }>
  total_rows: number
  account_mismatch: boolean
  csv_account_id: string
  expected_account_id: string
  format_invalid: boolean
  detected_format: string
  missing_columns: string[]
  import_batch: string | null
  cash_sync?: { success: boolean; imported?: number; skipped?: number; error?: string }
}

export interface ImportBatch {
  import_batch: string
  broker_account_id: number
  trade_count: number
  imported_at: string | null
}

export interface FlexAccountStatus {
  account: string
  alias: string
  ib_account_id: string
  configured: boolean
  cash_configured: boolean
  flex_query_id: string | null
  flex_cash_query_id: string | null
  token_preview: string | null
}

export const getLedgerPositions = (params: {
  tab?: string
  status?: string
  underlying?: string
  search?: string
  account?: string
  limit?: number
  offset?: number
}) => apiGet<LedgerPositionsResponse>('/ledger/positions', { params })

export const getLedgerSummary = (params?: { account?: string }) =>
  apiGet<LedgerSummary>('/ledger/summary', { params })

export const hideLedgerPosition = (positionId: number) =>
  apiPost<{ success: boolean }>(`/ledger/positions/${positionId}/hide`)

export const unhideLedgerPosition = (positionId: number) =>
  apiPost<{ success: boolean }>(`/ledger/positions/${positionId}/unhide`)

export const getCashTransactions = (params?: { type?: string; limit?: number; account?: string }) =>
  apiGet<{ total_amount: number; count: number; transactions: CashTransaction[] }>(
    '/ledger/cash-transactions',
    { params },
  )

export const syncCashTransactions = (account: string) =>
  apiPost<{ success: boolean; imported: number; skipped: number; total_fetched: number }>(
    '/ledger/cash-transactions/sync',
    { account },
  )

async function uploadCsv(path: string, account: string, file: File): Promise<ImportPreviewResult> {
  const form = new FormData()
  form.append('account', account)
  form.append('file', file)
  const { data } = await client.post<ImportPreviewResult>(path, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120_000,
  })
  return data
}

export const previewLedgerImport = (account: string, file: File) =>
  uploadCsv('/ledger/import/preview', account, file)

export const commitLedgerImport = (account: string, file: File) =>
  uploadCsv('/ledger/import/commit', account, file)

export const runFlexImport = (account: string) =>
  apiPost<ImportPreviewResult>('/ledger/import/flex', { account }, { timeout: 180_000 })

export const getImportHistory = (limit = 50) =>
  apiGet<{ batches: ImportBatch[] }>('/ledger/import/history', { params: { limit } })

export const deleteImportBatch = (importBatch: string) =>
  apiDelete<{ success: boolean; raw_deleted: number; positions_deleted: number }>(
    `/ledger/import/${encodeURIComponent(importBatch)}`,
  )

export const getFlexStatus = () =>
  apiGet<{ accounts: FlexAccountStatus[] }>('/ledger/flex/status')
